import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Icons from 'lucide-react';
import { BookOpen, Printer, PlayCircle, Search, X, ZoomIn } from 'lucide-react';
import SpeakButton from '../components/SpeakButton';
import { useAuth } from '../context/AuthContext';
import { useI18n, LANG_LABELS } from '../i18n/I18nContext';
import { PageHeader } from '../components/common';
import { GUIDE, sectionsFor } from '../help/guideContent';
import { openWelcomeTour } from '../components/WelcomeTour';

function GuideSection({ sec, content, index, lang, onZoom, t }) {
  const Icon = Icons[sec.icon] || BookOpen;
  return (
    <section id={`guide-${sec.id}`} className="kmt-panel p-5 md:p-6 kmt-guide-section" data-testid={`guide-section-${sec.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--kmt-gold)' }}>
          <Icon size={18} />
        </div>
        <h2 className="font-display font-semibold text-base md:text-lg flex-1 min-w-0">{index}. {content.title}</h2>
        <SpeakButton text={`${content.title}. ${content.steps.map((s, i) => `${i + 1}. ${s}`).join(' ')}`} lang={lang} testid={`guide-listen-${sec.id}`} />
      </div>
      <button onClick={() => onZoom(sec.id)} className="relative block mb-4 rounded-lg overflow-hidden group" style={{ border: '1px solid var(--kmt-border)' }}
        data-testid={`guide-image-${sec.id}`} aria-label={t('help.zoom')}>
        <img src={`/guide/${sec.id}.jpg`} alt={content.title} loading="lazy" width="390" height="844" className="block w-48 sm:w-56 h-auto" style={{ aspectRatio: "390 / 844" }} />
        <span className="absolute bottom-1.5 right-1.5 rounded-full p-1.5 kmt-no-print" style={{ background: 'rgba(0,0,0,0.7)', color: 'var(--kmt-gold)' }}><ZoomIn size={14} /></span>
      </button>
      <ol className="space-y-3">
        {content.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="font-mono-k text-xs w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ border: '1px solid var(--kmt-border)', color: 'var(--kmt-gold)' }}>{i + 1}</span>
            <span style={{ color: 'var(--kmt-text)' }}>{s}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function HelpGuide() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [q, setQ] = useState('');
  const [zoom, setZoom] = useState(null);
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return undefined;
    const timers = [300, 1200, 2500].map((ms) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  const guide = GUIDE[lang] || GUIDE.EN;
  const sections = useMemo(() => sectionsFor(user?.role || 'OPERATOR'), [user?.role]);
  const shown = sections.filter((s) => {
    if (!q.trim()) return true;
    const c = guide[s.id];
    const hay = `${c.title} ${c.steps.join(' ')} ${GUIDE.EN[s.id].title} ${GUIDE.EN[s.id].steps.join(' ')}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="kmt-fade-in" data-testid="help-guide-page">
      <div className="kmt-no-print">
        <PageHeader icon={BookOpen} title={t('help.title')} subtitle={t('help.subtitle')}
          right={<>
            <button className="kmt-btn-ghost flex items-center gap-2" onClick={openWelcomeTour} data-testid="help-replay-tour-btn"><PlayCircle size={15} />{t('help.replay')}</button>
            <button className="kmt-btn-gold flex items-center gap-2" onClick={() => window.print()} data-testid="help-download-pdf-btn"><Printer size={15} />{t('help.pdf')}</button>
          </>} />
      </div>
      <div className="p-4 md:p-8 max-w-4xl space-y-5">
        <div className="kmt-print-only font-display font-bold text-xl">KMT Factory — {t('help.title')} ({user?.role})</div>
        <div className="flex flex-wrap items-center gap-3 kmt-no-print">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted-2)' }} />
            <input className="kmt-input" style={{ paddingLeft: 36 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('help.search')} data-testid="help-search-input" />
          </div>
          <div className="inline-flex rounded-lg p-1 gap-1" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
            {Object.keys(LANG_LABELS).map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`help-lang-${l}`} className="px-3 py-1.5 rounded-md text-sm"
                style={lang === l ? { background: 'var(--kmt-gold)', color: '#111' } : { color: 'var(--kmt-muted)' }}>{l === 'EN' ? 'English' : l === 'HI' ? 'हिन्दी' : 'ਪੰਜਾਬੀ'}</button>
            ))}
          </div>
        </div>
        <nav className="kmt-panel p-4 flex flex-wrap gap-2 kmt-no-print" data-testid="help-toc">
          {sections.map((s, i) => (
            <a key={s.id} href={`#guide-${s.id}`} className="text-xs px-2.5 py-1 rounded-full" style={{ border: '1px solid var(--kmt-border)', color: 'var(--kmt-muted)' }} data-testid={`help-toc-${s.id}`}
              onClick={(e) => { e.preventDefault(); document.getElementById(`guide-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
              {i + 1}. {guide[s.id].title}
            </a>
          ))}
        </nav>
        {shown.length === 0 && <div className="text-sm" style={{ color: 'var(--kmt-muted)' }} data-testid="help-no-results">{t('help.noResults')}</div>}
        {shown.map((s) => <GuideSection key={s.id} sec={s} content={guide[s.id]} index={sections.indexOf(s) + 1} lang={lang} onZoom={setZoom} t={t} />)}
      </div>
      {zoom && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 kmt-no-print" style={{ background: 'rgba(0,0,0,0.88)' }} onClick={() => setZoom(null)} data-testid="guide-image-zoom">
          <button className="absolute top-4 right-4 rounded-full p-2" style={{ background: 'var(--kmt-panel-2)', color: 'var(--kmt-text)' }} onClick={() => setZoom(null)} data-testid="guide-image-zoom-close"><X size={18} /></button>
          <img src={`/guide/${zoom}.jpg`} alt="" className="max-h-[90vh] max-w-full rounded-lg" style={{ border: '1px solid var(--kmt-border)' }} />
        </div>,
        document.body,
      )}
    </div>
  );
}
